import type { MultipleQueriesQuery, MultipleQueriesResponse } from "@algolia/client-search";
import type { SearchClient } from "algoliasearch/lite";
import algoliasearch from "algoliasearch/lite";
import axios from "axios";
import type { ComputedRef, InjectionKey } from "vue";
import { computed, inject, provide, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useTenantEntityManager } from "@/composables/useTenantManager.js";
import { API_ENTITY_INFORMATION } from "@/constants";
import { getApiClientWithAuth } from "@/services/API";
import { profileServiceRequest } from "@/services/utils";
import { resolveOktaAccessToken } from "@/util/auth";
import type {
  CustomSearchClient,
  CustomSearchRequest,
  SearchClientConfig,
  SearchClientInstance,
} from "./types/searchClient";
import { useExchangeMode } from "./useExchangeMode";

export const DefaultClientConfig: SearchClientConfig = {
  useProxy: true,
  useLaravelBackend: false,
  hitsPerPage: 50,
  hiddenFilters: "",
  addedParams: {},
  proxyConfig: {
    base: "",
    laravelPath: "",
    query: "",
    facet: "",
    export: "",
    totals: "",
  },
};

const buildEmptyResults = <TObject>(): MultipleQueriesResponse<TObject> => {
  return {
    results: [
      {
        hits: [],
        nbHits: 0,
        page: 0,
        nbPages: 0,
        hitsPerPage: 50,
        facets: {},
        query: "",
        params: "",
        index: "",
        processingTimeMS: 0,
        exhaustiveNbHits: true,
      },
    ],
  };
};

const searchClientKey: InjectionKey<SearchClientInstance> = Symbol("apic.searchClient");

const createSearchClient = (searchConfig: SearchClientConfig): SearchClientInstance => {
  const router = useRouter();
  const store = useStore();
  const { getSelectedEntityView: selectedEntity } = useTenantEntityManager();

  const config = ref<SearchClientConfig>(structuredClone(searchConfig));
  const searchRequests = ref<readonly MultipleQueriesQuery[]>([]);

  const { exchangeMode } = useExchangeMode();

  const currentUser = computed(() => store.getters.getCurrentUser);
  const selectedEntityId = computed<string>(() => {
    const selectedEntityId: string | undefined = selectedEntity.value?.id;
    if (selectedEntityId !== undefined) {
      return selectedEntityId;
    }
    return API_ENTITY_INFORMATION.id;
  });

  const authToken = computed(() => resolveOktaAccessToken(currentUser.value));

  const createDefaultSearchClient = (): SearchClient =>
    algoliasearch(import.meta.env.VITE_ALGOLIA_APP_ID, import.meta.env.VITE_ALGOLIA_API_KEY);

  const apiClient = axios.create({
    headers: {
      "Cache-Control": "no-cache, no-store, must-revalidate",
      Pragma: "no-cache",
      Expires: "0",
      "Content-Type": "application/json",
      ...(authToken.value && { Authorization: `Bearer ${authToken.value}` }),
    },
  });

  // baseURL is resolved from the live config at request time so configure() updates take
  // effect without recreating the axios client. Auth header stays at create-time pending
  // the upcoming token-refresh ticket.
  apiClient.interceptors.request.use((requestConfig) => {
    requestConfig.baseURL = config.value.proxyConfig.base;
    return requestConfig;
  });

  const fixupPayload = (): CustomSearchRequest => {
    return {
      requests: searchRequests.value.map((request) => {
        let filters = request?.params?.filters;
        if (config?.value?.hiddenFilters?.length > 0) {
          if ((filters ?? "").length > 0) {
            filters = `${filters} AND ${config?.value?.hiddenFilters}`;
          } else {
            filters = config?.value?.hiddenFilters;
          }
        }
        return {
          indexName: request.indexName,
          ...request.params,
          filters,
          hitsPerPage: config.value.hitsPerPage,
        };
      }),
    };
  };

  const queryParams = (): string => {
    const tenant_entity_id = selectedEntityId.value;
    const exchange_mode = exchangeMode.value;
    // Only context params belong in the proxy URL — the actual search (query, filters, facets)
    // travels in the POST body via fixupPayload(). Spreading route.query here previously leaked a
    // one-cycle-stale `search` value, since router.replace lands after the search request fires.
    const routeQuery = {
      query: {
        tenant_entity_id,
        exchange_mode,
        ts: Date.now(),
        ...config.value.addedParams,
      },
    };
    const href = router.resolve(routeQuery).href;
    const queryIndex = href.indexOf("?");
    return queryIndex >= 0 ? href.slice(queryIndex) : "";
  };

  const customSearchClientDirect = computed<CustomSearchClient>(() => ({
    extra: { tenant_entity_id: selectedEntityId.value },
    search: async <T>(queries: readonly MultipleQueriesQuery[]): Promise<MultipleQueriesResponse<T>> => {
      try {
        searchRequests.value = queries;
        const searchQuery = config.value.proxyConfig.query;
        const { data } = await apiClient.post<MultipleQueriesResponse<T>>(
          `${searchQuery}/${queryParams()}`,
          fixupPayload(),
        );
        return data;
      } catch (error) {
        console.error("Search API error:", error);
        return buildEmptyResults<T>();
      }
    },
    searchForFacetValues: async (requests) => {
      try {
        const facetQuery = config.value.proxyConfig.facet;
        const { data } = await getApiClientWithAuth(config.value.proxyConfig.base).post(
          `${facetQuery}/${queryParams()}`,
          {
            requests: requests,
          },
        );
        return data;
      } catch (error) {
        console.error("Facet API error:", error);
        return {
          facetHits: [],
          exhaustiveFacetsCount: true,
          processingTimeMS: 0,
        };
      }
    },
  }));

  const customSearchClientLaravel = computed<CustomSearchClient>(() => ({
    extra: { tenant_entity_id: selectedEntityId.value },
    search: async <T>(queries: readonly MultipleQueriesQuery[]): Promise<MultipleQueriesResponse<T>> => {
      try {
        searchRequests.value = queries;
        const { data } = await profileServiceRequest({
          method: "post",
          url: `${config.value.proxyConfig.laravelPath}${config.value.proxyConfig.query}${queryParams()}`,
          payload: fixupPayload(),
        });
        return data;
      } catch (error) {
        console.error("Search API error:", error);
        return buildEmptyResults<T>();
      }
    },
    searchForFacetValues: async (requests) => {
      try {
        const { data } = await profileServiceRequest({
          method: "post",
          url: `${config.value.proxyConfig.laravelPath}${config.value.proxyConfig.facet}${queryParams()}`,
          payload: { requests: requests },
        });
        return data;
      } catch (error) {
        console.error("Facet API error:", error);
        return {
          facetHits: [],
          exhaustiveFacetsCount: true,
          processingTimeMS: 0,
        };
      }
    },
  }));

  const currentConfig = (): SearchClientConfig => {
    return config.value;
  };

  const configure = (updatedConfig: SearchClientConfig): void => {
    config.value = structuredClone(updatedConfig);
  };

  const updateHiddenFilters = (filters: string): void => {
    config.value = { ...structuredClone(config.value), hiddenFilters: filters };
  };

  const getHiddenFilters = (): string => {
    return config.value.hiddenFilters;
  };

  const setHitsPerPage = (hitCount: number): void => {
    config.value = { ...structuredClone(config.value), hitsPerPage: hitCount };
  };

  const getHitsPerPage = (): number => {
    return config.value.hitsPerPage;
  };

  const searchClient: ComputedRef<CustomSearchClient> = config.value.useProxy
    ? config.value.useLaravelBackend
      ? customSearchClientLaravel
      : customSearchClientDirect
    : computed(() => createDefaultSearchClient());

  return {
    searchClient,
    configure,
    currentConfig,
    fixupPayload,
    queryParams,
    setHitsPerPage,
    getHitsPerPage,
    updateHiddenFilters,
    getHiddenFilters,
  };
};

/**
 * Composable to dynamically choose between a direct Algolia search client
 * or a custom proxy-based search client.
 *
 * Two call modes:
 *  - Provider: pass a `searchConfig` from the search root (MyStudentsSearchComponent).
 *    Creates the instance and exposes it to descendants via Vue's provide().
 *  - Consumer: call with no arg from a descendant. Resolves the provided instance via inject().
 *
 * Note: We have some pre defined filters and hits per page special for acceptance and approvals page,
 * make sure new usage does not break it.
 *
 * @see {@link https://www.algolia.com/doc/guides/building-search-ui/going-further/backend-search/in-depth/backend-instantsearch/vue/#searching-for-results|Custom Algolia Endpoint}
 */
export const useSearchClient = (searchConfig?: SearchClientConfig): SearchClientInstance => {
  if (searchConfig) {
    const instance = createSearchClient(searchConfig);
    provide(searchClientKey, instance);
    return instance;
  }

  const instance = inject(searchClientKey);
  if (!instance) {
    throw new Error(
      "useSearchClient() called without a config and no provider was found in the component tree. " +
        "Ensure an ancestor component called useSearchClient(config) first.",
    );
  }
  return instance;
};
